import { PermissionConstants } from "./constants/PermissionConstants";
import { RouteConstants } from "./constants/RouteConstants";

export type AppRouteNav = {
  /** Top-level nav label, or parent label when this route is a sub-link */
  label: string;
  /** When set, this route appears as a sub-link under the named parent */
  group?: string;
  order?: number;
  /** Only show in nav for these environments (omit = all) */
  envOnly?: string[];
};

export type AppRouteDef = {
  path: RouteConstants;
  /** Any-of; empty list = any authenticated user */
  permissions: PermissionConstants[];
  exact?: boolean;
  nav?: AppRouteNav;
  /** Lower number = preferred default landing route */
  defaultPriority?: number;
};

export const APP_ROUTE_DEFS: AppRouteDef[] = [
  {
    path: RouteConstants.BASE,
    permissions: [],
    exact: true,
  },
  {
    path: RouteConstants.EYEBALL_SESSIONS,
    permissions: [PermissionConstants.SESSION_READ_SELF, PermissionConstants.SESSION_READ_ALL],
    exact: true,
    nav: { label: "Sessions", order: 1 },
    defaultPriority: 1,
  },
  {
    path: RouteConstants.EYEBALL_SESSIONS_MANAGE,
    permissions: [
      PermissionConstants.SESSION_MGMT_READ_SELF,
      PermissionConstants.SESSION_MGMT_READ_ALL,
      PermissionConstants.SESSION_ASSIGN_TEAM,
      PermissionConstants.SESSION_ASSIGN_ALL,
    ],
    exact: true,
    defaultPriority: 2,
    nav: { label: "Session Requests", group: "Manage", order: 0 },
  },
  {
    path: RouteConstants.EYEBALL_SESSION_VIEWER,
    permissions: [
      PermissionConstants.SESSION_READ_SELF,
      PermissionConstants.SESSION_READ_ALL,
      PermissionConstants.SESSION_MGMT_READ_SELF,
      PermissionConstants.SESSION_MGMT_READ_ALL,
    ],
    exact: true,
  },
  {
    path: RouteConstants.EYEBALL_SESSION_COMPARE,
    permissions: [PermissionConstants.SESSION_READ_SELF, PermissionConstants.SESSION_READ_ALL],
    exact: true,
  },
  {
    path: RouteConstants.TRAINING,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
    nav: { label: "Training", order: 2, envOnly: ["prod", "local"] },
  },
  {
    path: RouteConstants.TRAINING_LIVE,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
  },
  {
    path: RouteConstants.TRAINING_CANNED_GAME,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
  },
  {
    path: RouteConstants.TRAINING_LIVE_GAME,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
  },
  {
    path: RouteConstants.TRAINING_REVIEW,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
  },
  {
    path: RouteConstants.TRAINING_SESSION_REVIEW,
    permissions: [PermissionConstants.TRAINING_READ, PermissionConstants.TRAINING_WRITE],
    exact: true,
  },
  {
    path: RouteConstants.INVENTORY,
    permissions: [PermissionConstants.INVENTORY_READ, PermissionConstants.INVENTORY_WRITE],
    exact: true,
    nav: { label: "Inventory", order: 3 },
    defaultPriority: 3,
  },
  {
    path: RouteConstants.INVENTORY_RFID,
    permissions: [PermissionConstants.RFID_READ],
    exact: true,
    nav: { label: "Rfid", group: "Inventory", order: 1 },
  },
  {
    path: RouteConstants.INVENTORY_RFID_PLAY_CONFIDENCE,
    permissions: [PermissionConstants.RFID_READ],
    exact: true,
    nav: { label: "Rfid Scores", group: "Inventory", order: 2 },
  },
  {
    path: RouteConstants.INVENTORY_RFID_READER_DATA,
    permissions: [PermissionConstants.RFID_READ],
    exact: true,
    nav: { label: "Rfid Reader Accuracy", group: "Inventory", order: 3 },
  },
  {
    path: RouteConstants.MILESTONES,
    permissions: [PermissionConstants.MILESTONES_READ, PermissionConstants.MILESTONES_WRITE],
    exact: true,
    nav: { label: "Milestones", order: 4 },
    defaultPriority: 4,
  },
  {
    path: RouteConstants.MANAGE,
    permissions: [
      PermissionConstants.VENUE_READ_SELF,
      PermissionConstants.VENUE_READ_ALL,
      PermissionConstants.AUTOGRAPHER_WRITE,
      PermissionConstants.ROLL_READ,
      PermissionConstants.ROLL_WRITE,
      PermissionConstants.USER_ADMIN_READ,
      PermissionConstants.USER_ADMIN_WRITE,
      PermissionConstants.SESSION_ASSIGN_TEAM,
      PermissionConstants.SESSION_ASSIGN_ALL,
      PermissionConstants.SESSION_MGMT_READ_SELF,
      PermissionConstants.SESSION_MGMT_READ_ALL,
    ],
    exact: true,
    nav: { label: "Manage", order: 5 },
  },
  {
    path: RouteConstants.MANAGE_AUTOGRAPHERS,
    permissions: [PermissionConstants.AUTOGRAPHER_WRITE],
    exact: true,
    nav: { label: "Autographers", group: "Manage", order: 1 },
  },
  {
    path: RouteConstants.MANAGE_EYEBALL_SYNC_REQUESTS,
    permissions: [PermissionConstants.SESSION_SYNC_ADMIN],
    exact: true,
    nav: { label: "Eyeball Sync Requests", group: "Manage", order: 1 },
  },
  {
    path: RouteConstants.MIGRATION_INTEGRITY,
    permissions: [PermissionConstants.MIGRATION_READ],
    exact: true,
    nav: { label: "Migration Integrity", group: "Manage", order: 2 },
  },
  {
    path: RouteConstants.MANAGE_ROLLS,
    permissions: [PermissionConstants.ROLL_READ, PermissionConstants.ROLL_WRITE],
    exact: true,
    nav: { label: "Rolls", group: "Manage", order: 3 },
  },
  {
    path: RouteConstants.MANAGE_ASSIGNED_ROLLS,
    permissions: [PermissionConstants.ROLL_WRITE],
    exact: true,
    nav: { label: "Assigned Rolls", group: "Manage", order: 4 },
  },
  {
    path: RouteConstants.MANAGE_LOCATION_TEAMS,
    permissions: [PermissionConstants.VENUE_WRITE_ALL, PermissionConstants.ROLL_WRITE],
    exact: true,
    nav: { label: "Location Teams", group: "Manage", order: 5 },
  },
  {
    path: RouteConstants.MANAGE_LICENSEES,
    permissions: [PermissionConstants.USER_ADMIN_READ, PermissionConstants.USER_ADMIN_WRITE],
    exact: true,
    nav: { label: "Licensees", group: "Manage", order: 6 },
  },
  {
    path: RouteConstants.SETTINGS,
    permissions: [PermissionConstants.USER_ADMIN_READ, PermissionConstants.USER_ADMIN_WRITE],
    exact: true,
    nav: { label: "Settings", order: 6 },
  },
];

export const canAccessByPermissions = (
  userPermissions: string[],
  required: PermissionConstants[] | string[],
): boolean => {
  if (required.length === 0) return true;
  return required.some((permission) => userPermissions.includes(permission));
};

export const getDefaultRouteFromPermissions = (userPermissions: string[]): RouteConstants => {
  const match = APP_ROUTE_DEFS.filter(
    (route) => route.defaultPriority != null && canAccessByPermissions(userPermissions, route.permissions),
  ).sort((a, b) => (a.defaultPriority ?? 99) - (b.defaultPriority ?? 99))[0];

  return match?.path ?? RouteConstants.BASE;
};

export type NavLinkBuilt = {
  name: string;
  to: string;
  visible: boolean;
  subLinks?: { name: string; to: string; visible: boolean }[];
};

export const buildNavigationLinks = (userPermissions: string[], env: string): NavLinkBuilt[] => {
  const topLevel = APP_ROUTE_DEFS.filter(
    (route) => route.nav && !route.nav.group && canAccessByPermissions(userPermissions, route.permissions),
  ).sort((a, b) => (a.nav?.order ?? 99) - (b.nav?.order ?? 99));

  return topLevel
    .filter((route) => {
      const envOnly = route.nav?.envOnly;
      return !envOnly || envOnly.includes(env);
    })
    .map((route) => {
      const label = route.nav!.label;
      const subLinks = APP_ROUTE_DEFS.filter(
        (sub) => sub.nav?.group === label && canAccessByPermissions(userPermissions, sub.permissions),
      )
        .sort((a, b) => (a.nav?.order ?? 99) - (b.nav?.order ?? 99))
        .map((sub) => ({
          name: sub.nav!.label,
          to: sub.path,
          visible: true,
        }));

      return {
        name: label,
        to: route.path,
        visible: true,
        subLinks: subLinks.length > 0 ? subLinks : undefined,
      };
    });
};
